/*
 * Module for showing a task to complete in the reviewer UI
 */

define([
  "marionette",
  "jquery",
  "messaging/messenger",
  "modules/reviewer/reviewerArea/reviewerAreaView",
  "modules/topicsSplit/topic/topicResponse/topicResponseProperties/topicResponsePropertiesModule",
  "modules/topicsSplit/topic/topicResponse/topicResponsePreview/topicResponsePreviewModule",
  "modules/topicsSplit/topic/topicResponse/topicAttributes/topicAttributesModule",
  "modules/topicsSplit/topic/topicResponse/topicCategories/topicCategoriesModule",
  "modules/topicsSplit/topic/topicResponse/topicCategories/topicCategoriesHeader/topicCategoriesHeaderModule",
  "modules/header/kbMenu/kbMenuModel",
  "modules/topicsSplit/topic/topicModel",
  "modules/tasks/tasksModel",
  "modules/topicsSplit/topic/topicExamples/topicExamplesModule",
  "modules/tasks/taskUploads/taskUploadsModule",
], function (
  Marionette,
  $,
  Messenger,
  ReviewerAreaView,
  TopicResponsePropertiesModule,
  TopicResponsePreviewModule,
  TopicAttributesModule,
  TopicCategoriesModule,
  TopicCategoriesHeaderModule,
  KbMenuModel,
  TopicModel,
  TaskModel,
  TopicExamplesModule,
  TaskUploadsModule,
) {
  "use strict";

  return Marionette.Controller.extend({
    initialize: function (options) {
      this.name = "reviewerArea";
      this.region = options.region;
      this._setUpReqres();
      this._setUpVents();
    },
    show: function (topic, taskId) {
      this.topic = topic;
      //add the task with the passed task ID as an attribute of the topic Model
      topic.reviewerAreaTask = topic.tasks().get(taskId);
      topic.isTenantAdmin = Messenger.reqres.request("user:is:admin");
      topic.isAuthor = Messenger.reqres.request("user:is:author") || topic.isTenantAdmin;
      topic.isReviewerWithAttributes =
        Messenger.reqres.request("user:is:reviewerWithAttributes") || topic.isAuthor;
      topic.allowCategories = topic.isReviewerWithAttributes && !topic.isNew();
      topic.allowAttributes = topic.allowCategories;
      topic.allowExamples = Messenger.reqres.request("can:view:topicExamples");
      const touchpointList = Messenger.reqres.request("kb:touchpoints");
      let touchpointAccess = false;
      let i, j;
      if (
        topic.reviewerAreaTask.get("type") == TaskModel.TYPE.ADVANCED ||
        topic.reviewerAreaTask.get("type") == TaskModel.TYPE.TASK_AND_STEP
      ) {
        const taskTouchpointIds = topic.reviewerAreaTask.get("touchpointIds");
        if (!taskTouchpointIds || !taskTouchpointIds.length) {
          // If there are no specific touchpoints, then all touchpoints are allowed
          touchpointAccess = true;
        } else {
          for (i = 0; i < touchpointList.length; ++i) {
            for (j = 0; j < taskTouchpointIds.length; ++j) {
              if (touchpointList.at(i).get("touchPointId") == taskTouchpointIds[j]) {
                touchpointAccess = true;
                break;
              }
            }
          }
        }
        topic.allowUploads = topic.reviewerAreaTask.uploads().length > 0;
      } else if (topic.reviewerAreaTask.get("type") == TaskModel.TYPE.TOPIC_REVIEW) {
        // Topic Review does not have a particular TouchPoint
        touchpointAccess = true;
      } else {
        for (i = 0; i < touchpointList.length; i++) {
          if (
            touchpointList.models[i].get("touchPointId") ===
            topic.reviewerAreaTask.get("touchpointId")
          ) {
            touchpointAccess = true;
          }
        }
      }
      topic.hasAccess = touchpointAccess;
      this.layout = new ReviewerAreaView.reviewerAreaLayout({ model: topic });
      let regionEntity = topic;
      if (touchpointAccess && !topic.isNew()) {
        this.responseProperties = this.addSubModule(
          new TopicResponsePropertiesModule({ region: this.layout.responseProperties }),
        );
        this.responsePreview = this.addSubModule(
          new TopicResponsePreviewModule({ region: this.layout.responsePreview }),
        );
        if (topic.allowUploads) {
          this.uploads = this.addSubModule(
            new TaskUploadsModule({
              region: this.layout.uploads,
            }),
          );
        }
        if (topic.allowExamples) {
          this.examples = this.addSubModule(
            new TopicExamplesModule({
              region: this.layout.examples,
              topic: this.topic,
              readOnly: true,
            }),
          );
        }
        if (topic.isReviewerWithAttributes) {
          this.categories = this.addSubModule(
            new TopicCategoriesModule({ region: this.layout.categories }),
          );
          this.categoriesHeader = this.addSubModule(
            new TopicCategoriesHeaderModule({
              region: this.layout.categoriesHeader,
            }),
          );
          this.attributes = this.addSubModule(
            new TopicAttributesModule({ region: this.layout.topicAttributes }),
          );
        }
        //if the conversation model fails to load, reload the tasks so force getting current data
        topic.conversation().once(
          "error",
          function () {
            Messenger.vent.trigger("show:reviewer:list", topic.reviewerAreaTask.get("status"));
          },
          this,
        );
        regionEntity = topic.conversation();
      }
      this.layout.on(
        "show:topic",
        function () {
          const newTopic = topic.isNew();
          Messenger.save(function (success) {
            if (success) {
              if (newTopic) {
                // Must explicitly save New topics, they are skipped when saving the collection
                topic.saveModel({
                  success: function () {
                    Messenger.saveAttempted(true);
                    topic.reviewerAreaTask.set("intentId", topic.get("intentId"));
                    topic.reviewerAreaTask.set("status", TaskModel.STATUS.IN_PROGRESS);
                    topic.reviewerAreaTask.saveModel({
                      success: function (model) {
                        model.dirty = false;
                        Messenger.vent.trigger("show:topic", topic);
                      },
                    });
                  },
                });
              } else {
                Messenger.vent.trigger("show:topic", topic);
              }
            }
          }, this);
        },
        this,
      );

      this.layout.on(
        "show:attributes",
        function () {
          if (this.attributes) {
            this.attributes.show(
              topic.conversation().topicAttributes(),
              this.isCompleteStep(topic),
            );
          }
        },
        this,
      );

      this.layout.on(
        "show:categories",
        function () {
          if (this.categories) {
            this.categories.show(topic.conversation().categories());
            this.categoriesHeader.show(
              topic.conversation().categories(),
              this.isCompleteStep(topic),
            );
          }
        },
        this,
      );

      this.layout.on(
        "show:examples",
        function () {
          if (this.examples) {
            this.examples.show(topic.examples());
          }
        },
        this,
      );

      this.layout.on(
        "show:preview",
        async function () {
          await this._showPreview(topic);
        },
        this,
      );

      this.layout.on("show:uploads", () => {
        if (this.uploads) {
          this.uploads.show(topic.reviewerAreaTask, topic.reviewerAreaTask.uploads());
        }
      });

      this.layout.on(
        "start:progress",
        async function () {
          this._beginTask();
          Messenger.save(function (success) {
            if (success) {
              this.region.reset();
            }
          }, this);
          //trigger a sync on the topic to re-render
          this.topic.trigger("sync");
          //Re-render the layout and preview
          this.layout.render();
          await this._showPreview(topic);
        },
        this,
      );

      this.layout.on(
        "complete:update",
        function () {
          this._completeTask(topic);
        },
        this,
      );

      this.layout.on(
        "complete:review",
        function () {
          this._completeTask(topic);
        },
        this,
      );

      this.layout.on(
        "reject:review",
        function () {
          this._rejectTask(topic);
        },
        this,
      );

      this.layout.on(
        "reject:update",
        function () {
          this._rejectTask(topic);
        },
        this,
      );

      this.layout.on("add:comment", this._onAddComment, this);

      this.layout.on(
        "show",
        async function () {
          if (!topic.isNew()) {
            this._decideResponseTabs();
            await this._showPreview(topic);
          }
        },
        this,
      );
      this.layout.on(
        "reset:task:user",
        function () {
          this.layout.setAssignee(topic.reviewerAreaTask.get("user"));
        },
        this,
      );

      this.showRegion(this.layout, {
        loading: true,
        entity: regionEntity,
      });
    },
    _beginTask: function () {
      const task = this.topic.reviewerAreaTask;
      task.set("status", TaskModel.STATUS.IN_PROGRESS);
    },
    _completeTask: function (topic) {
      const callbacks = {};
      callbacks.confirm = function (comment) {
        const task = topic.reviewerAreaTask;
        //update the status of the task
        task.set("status", TaskModel.STATUS.COMPLETED);
        task.addComment(comment);
        Messenger.save(function (success) {
          if (success) {
            this.region.reset();
          }
        }, this);

        //trigger a sync on the topic to re-render
        topic.trigger("sync");
      };
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      callbacks.cancel = function () {};
      const message =
        topic.reviewerAreaTask.get("type") === 0
          ? $.t("task.topicReview.acceptWithComment")
          : $.t("task.standard.acceptWithComment");
      const button =
        topic.reviewerAreaTask.get("type") === 0
          ? $.t("task.status.approve")
          : $.t("task.status.complete");
      Messenger.confirmDialog(message, callbacks, this, button, true);
    },
    _decideResponseTabs: function () {
      //decide if we show the categories and attributes tabs
      if (this.categories) {
        const catOptions = Messenger.reqres.request("kb:categories");
        catOptions.onSynced(function () {
          let showCategories = false;
          // validate that there are categories and at least one with category values
          if (catOptions.length > 0) {
            catOptions.each(function (cat) {
              if (cat.getValues().length !== 0) {
                showCategories = true;
              }
            });
          }
          if (showCategories) {
            this.layout.showCategoriesTab();
          } else {
            this.layout.hideCategoriesTab();
          }
        }, this);
      }
      // TODO - Always show attributes or have feature toggle?
      if (this.attributes) {
        this.layout.showAttributes();
      }
    },
    _onAddComment: function () {
      const callbacks = {};
      const _this = this;
      callbacks.confirm = function (comment) {
        _this.topic.reviewerAreaTask.addComment(comment);
        _this.region.reset();
      };
      Messenger.confirmDialog($.t("task.addComment"), callbacks, this, "common.add", true);
    },
    _rejectTask: function (topic) {
      const callbacks = {};
      callbacks.confirm = function (comment) {
        const task = topic.reviewerAreaTask;
        //update the status of the task
        task.set("status", TaskModel.STATUS.FAILED);
        task.addComment(comment);
        Messenger.save(function (success) {
          if (success) {
            this.region.reset();
          }
        }, this);

        //trigger a sync on the topic to re-render
        topic.trigger("sync");
      };
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      callbacks.cancel = function () {};
      const message =
        topic.reviewerAreaTask.get("type") === 0
          ? $.t("task.topicReview.rejectWithComment")
          : $.t("task.standard.rejectWithComment");
      Messenger.confirmDialog(message, callbacks, this, "task.status.reject", true);
    },
    _showPreview: async function (topic) {
      //First boolean after liveConversation is "readOnly"; the second is "translationTask"
      //They get passed to "show" and put into the model
      switch (
        topic.reviewerAreaTask.get("type") //check the task type
      ) {
        case TaskModel.TYPE.TOPIC_REVIEW:
          if (this.responseProperties) {
            this.responseProperties.show(topic.conversation(), true, true);
          }
          if (this.responsePreview) {
            this.responsePreview.show({
              conversation: topic.conversation(),
              liveConversationRoot: topic.liveConversation(false),
              readOnly: true,
              translationTask: false,
              canEdit: true,
            });
          }
          break;
        case TaskModel.TYPE.UPDATE_RESPONSE:
          if (this.responseProperties) {
            this.responseProperties.show(topic.conversation(), this.isCompleteStep(topic), true);
          }
          if (this.responsePreview) {
            this.responsePreview.show({
              conversation: topic.conversation(),
              liveConversationRoot: topic.liveConversation(false),
              readOnly: this.isCompleteStep(topic),
              translationTask: false,
              startTouchPoint: topic.reviewerAreaTask.get("touchpointId"),
              startLanguage: topic.reviewerAreaTask.get("languageId"),
              canEdit: true,
            });
          }
          break;
        case TaskModel.TYPE.TRANSLATE_RESPONSE:
          if (this.responseProperties) {
            this.responseProperties.show(topic.conversation(), true, true);
          }
          if (this.responsePreview) {
            this.responsePreview.show({
              conversation: topic.conversation(),
              liveConversationRoot: topic.liveConversation(false),
              readOnly: true,
              translationTask: true,
              startTouchPoint: topic.reviewerAreaTask.get("touchpointId"),
              startLanguage: topic.reviewerAreaTask.get("languageId"),
              targetLanguage: topic.reviewerAreaTask.get("targetLanguageId"),
              canEdit: true,
            });
          }
          break;
        case TaskModel.TYPE.ADVANCED:
        case TaskModel.TYPE.TASK_AND_STEP:
          if (this.responseProperties) {
            this.responseProperties.show(topic.conversation(), this.isCompleteStep(topic), true);
          }
          if (this.responsePreview) {
            this.responsePreview.show({
              conversation: topic.conversation(),
              liveConversationRoot: topic.liveConversation(false),
              readOnly: this.isCompleteStep(topic),
              translationTask: false,
              startTouchPoint: topic.reviewerAreaTask.get("touchpointIds")[0],
              startLanguage: topic.reviewerAreaTask.get("languageId"),
              canEdit: await this._canEditTask(topic),
            });
          }
          break;
      }
    },
    isCompleteStep: function (topic) {
      let status = topic.reviewerAreaTask.get("status");
      switch (status) {
        case TaskModel.STATUS.COMPLETED:
        case TaskModel.STATUS.FAILED:
        case TaskModel.STATUS.IGNORED:
        case TaskModel.STATUS.RESUBMITTED:
          return true;
        case TaskModel.STATUS.IN_PROGRESS:
        case TaskModel.STATUS.NOT_STARTED:
        case TaskModel.STATUS.CREATED:
          return false;
      }
      return false;
    },
    _canEditTask: async function (topic) {
      var currentUser = Messenger.reqres.request("user");
      const kbId = Messenger.reqres.request("active:kb");
      var departments = Messenger.reqres.request("kb:departments", {
        kbId: kbId,
        includeUsers: true,
      });
      let assignee = topic.reviewerAreaTask.assignees().find((assignee) => assignee.get("userId"));

      if (!assignee) {
        await departments.onSyncedPromise();
        assignee = topic.reviewerAreaTask.assignees().some((data) => {
          var departmentUsers = departments.get(data.get("departmentId")).users();
          return departmentUsers.find((user) => user.get("userId") === currentUser.get("userId"));
        });
        if (assignee) {
          return true;
        } else {
          console.log("Logged in User is not an assignee: " + currentUser.get("userId"));
          return false;
        }
      } else if (assignee && assignee == currentUser.get("userId")) {
        console.log("Logged in User is not an assignee:" + currentUser.get("userId"));
        return false;
      }
      return assignee.get("canEdit");
    },
    _setUpReqres: function () {
      Messenger.reqres.setHandler("current:topic", () => this.topic, this);
      Messenger.reqres.setHandler("current:topic:id", () => this.topic.id, this);
      Messenger.reqres.setHandler("current:topic:status", () => this.topic.getStatus(), this);
    },
    _setUpVents: function () {
      Messenger.vent.on(
        "show:reviewer:list",
        function () {
          //if we switch the task type of the reviewer list, reset this area because the task loaded here is no longer on
          // the list  (even if the status was changed, a new version will appear on this list)
          this.region.reset();
        },
        this,
      );

      Messenger.vent.on(
        "selected:review",
        function (topic, taskId) {
          this.show(topic, taskId);
        },
        this,
      );

      //Refresh the preview when the response is changed, plus change the task status to In Progress
      //Plus, when the response changes and the topic status is Published, change the status to Pending Response
      Messenger.vent.on(
        "topic:response:changed",
        async function () {
          this._beginTask();

          //trigger a sync on the topic to re-render list
          this.topic.trigger("sync");
          //re-render the review area to update the response preview and status buttons
          this.layout.render();
          await this._showPreview(this.topic);

          //when response versioning is enabled and the current status is 'published', then change to 'pending-response'
          const kb = Messenger.reqres.request("active:kb:model");
          kb.onSynced(function () {
            if (
              kb.get("responseVersioning") &&
              this.topic.getStatus() === KbMenuModel.statuses.VERSIONED_PUBLISHED
            ) {
              this.topic.setStatus(KbMenuModel.statuses.VERSIONED_PENDING_RESPONSE);
            }
          }, this);
        },
        this,
      );

      Messenger.vent.on(
        "change:example:locale",
        function (localeId) {
          if (this.topic) {
            this.topic.selectedLocale = localeId;
          }
          if (this.examples) {
            this.examples.view.render();
            this.examples.view.setSelectedLocale(localeId);
          }
        },
        this,
      );
    },
  });
});
